import { cx } from '@linaria/core';
import { forwardRef, useRef, useState, useEffect } from 'react';
import { MoveFocusInside } from 'react-focus-on';
import { TransitionGroup } from 'react-transition-group';
import { useCourseRevisionState, useSaveRevision, disabledIdsToKey } from '@pm2/course-revisions';
import { floatingButtonStyles } from '@pm2/react-utils';
import { pm2SitemapStore, findRenderedDescriptorByPath, findPageDescriptorByPath, findSubNavigationRoot, findCourseDescriptorFromParents } from '@pm2/sitemap-utils';
import { pm2ConfigStore } from '@pm2/store';
import { useComponentFrameworkState } from '@provider/component-framework';
import { useLocation } from '@provider/navigation';
import { useUserIdentityState } from '@provider/user-identity';
import { breakpoints } from '@ui/css';
import { FloatingActionButton, NavigationIcon, GuideBubble, Pill, Tooltip, FloppyIcon } from '@ui/kit';
import { applyCssDeclarations } from '@utils/css';
import { useMatchBreakpoint, useIsomorphicLayoutEffect, HideFor, CSSRefTransition } from '@utils/react';
import { wrapperCls, buttonCls, FADE_DURATION_MS, iconCls, crossIcon, navigationCls, ASIDE_CONTENT_FADE_DURATION_MS, scrollerCls, srCloseButton, revisionsWrapper, pillCls, revisionButtonFadeDurationMs } from './sub-navigation.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { asideTokens } from '../../aside/styles/Aside.js';
import { SubNavigation } from '../../sub-navigation/sub-navigation.js';
import { Aside } from '../../aside/Aside.js';

const SubNavigationButton = /*#__PURE__*/forwardRef(function SubNavigationButton(props, ref) {
  const buttonRef = useRef(null);
  const sitemap = pm2SitemapStore.useSelector(state => state);
  const location = useLocation();
  const isEditingRevision = useCourseRevisionState(state => state.isEditing);
  const activePage = findRenderedDescriptorByPath(sitemap, location.pathname);
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const [isOpened, setIsOpened] = useState({
    explicitInteraction: false,
    byUser: true,
    bySubnavigationRoot: undefined
  });
  const currentPage = findPageDescriptorByPath(sitemap, location.pathname);
  const subNavigationRoot = findSubNavigationRoot(sitemap, location.pathname);
  const showCourseFrontpages = pm2ConfigStore.useSelector(state => state.config.settings.showCourseFrontpages) && activePage?.type === "Course";
  const hideButton = showCourseFrontpages || !subNavigationRoot?.children?.length;
  const label = isOpened.byUser || isOpened.bySubnavigationRoot?.current ? "Luk oversigt" : "Se oversigt";
  useEffect(() => {
    applyCssDeclarations(asideTokens.leftAsideWidth.__override(isEditingRevision ? "370px" : ""));
  }, [isEditingRevision]);
  useIsomorphicLayoutEffect(() => {
    if (isMobile) {
      // auto-collapse the panel when in mobile mode or if there is simply no
      // sub navigation available
      setIsOpened({
        explicitInteraction: false,
        byUser: false,
        bySubnavigationRoot: undefined
      });
      return;
    }

    // See ADO #93946 for specification as to when sub-nav should auto-open
    setIsOpened(prevState => ({
      explicitInteraction: false,
      byUser: prevState.byUser,
      bySubnavigationRoot: subNavigationRoot ? {
        id: subNavigationRoot.id,
        current: showCourseFrontpages && currentPage?.type === "Course" || prevState.bySubnavigationRoot?.id !== subNavigationRoot.id && subNavigationRoot.path === activePage?.path ? true : prevState.bySubnavigationRoot?.current ?? false
      } : undefined
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subNavigationRoot?.path, location.pathname, activePage?.path]);
  const toggleSubNavigation = options => {
    setIsOpened(prevState => ({
      explicitInteraction: options?.explicitInteraction ?? true,
      byUser: !(prevState.byUser || prevState.bySubnavigationRoot?.current),
      bySubnavigationRoot: prevState.bySubnavigationRoot ? {
        id: prevState.bySubnavigationRoot.id,
        current: false
      } : undefined
    }));
  };
  const handleClose = () => {
    setIsOpened(prevState => ({
      explicitInteraction: false,
      byUser: false,
      bySubnavigationRoot: prevState.bySubnavigationRoot ? {
        id: prevState.bySubnavigationRoot.id,
        current: false
      } : undefined
    }));
  };
  const handleKeyDown = evt => {
    if (evt.key === "Escape") {
      handleClose();
    }
  };
  const hasActivatedFrameworkComponent = useComponentFrameworkState(state => !!state.activeComponentId);
  useEffect(() => {
    if (hasActivatedFrameworkComponent) {
      handleClose();
    }
  }, [hasActivatedFrameworkComponent]);
  const isOpen = isOpened.byUser || isOpened.bySubnavigationRoot?.current;
  if (showCourseFrontpages) {
    return null;
  }
  return /*#__PURE__*/jsxs(HideFor, {
    breakpoint: breakpoints.mobile.andDown(),
    children: [/*#__PURE__*/jsx("div", {
      ref: ref,
      className: cx(wrapperCls, props.className),
      children: /*#__PURE__*/jsx(CSSRefTransition, {
        in: !hideButton,
        classNames: buttonCls,
        timeout: FADE_DURATION_MS,
        mountOnEnter: true,
        unmountOnExit: true,
        children: ref => /*#__PURE__*/jsxs("div", {
          ref: ref,
          className: floatingButtonStyles.buttonWrapperCls,
          children: [/*#__PURE__*/jsxs(FloatingActionButton, {
            ref: buttonRef,
            className: cx(buttonCls, floatingButtonStyles.buttonCls, isOpen && "active"),
            onClick: () => toggleSubNavigation({
              explicitInteraction: true
            }),
            tooltip: {
              placement: "right",
              body: label,
              ariaType: "label"
            },
            children: [/*#__PURE__*/jsx(FloatingActionButton.Icon, {
              contentKey: "burger",
              active: !isOpen,
              children: /*#__PURE__*/jsx(NavigationIcon, {
                className: iconCls
              })
            }), /*#__PURE__*/jsx(FloatingActionButton.Icon, {
              contentKey: "cross",
              active: isOpen,
              children: /*#__PURE__*/jsx("span", {
                className: crossIcon
              })
            })]
          }), /*#__PURE__*/jsx(GuideBubble, {
            id: "subNavigation",
            className: floatingButtonStyles.guideBubbleCls,
            popperPlacement: "right",
            label: "Læs mere om oversigten",
            title: "Se oversigt",
            hasUserInteracted: isOpen,
            children: "Klik p\xE5 knappen og se oversigten over forl\xF8bet eller siden. Her kan du hurtigt danne dig et overblik og du kan klikke dig videre til andre sider. Du kan lukke oversigten ved at klikke p\xE5 lukke-krydset."
          }), /*#__PURE__*/jsx(CourseRevisionButtons, {
            isOpen: !!isOpen,
            toggleSubNavigation: toggleSubNavigation
          })]
        })
      })
    }), /*#__PURE__*/jsx(Aside, {
      align: "left",
      expanded: isOpen && !hideButton,
      onClose: handleClose,
      hideFor: breakpoints.mobile.andDown(),
      children: /*#__PURE__*/jsx(TransitionGroup, {
        component: null,
        children: /*#__PURE__*/jsx(CSSRefTransition, {
          classNames: navigationCls,
          timeout: ASIDE_CONTENT_FADE_DURATION_MS,
          children: ref => /*#__PURE__*/jsx("div", {
            ref: ref,
            className: navigationCls,
            children: /*#__PURE__*/jsx(MoveFocusInside, {
              disabled: !isOpened.explicitInteraction,
              children: /*#__PURE__*/jsxs("div", {
                className: scrollerCls,
                onKeyDown: handleKeyDown,
                children: [/*#__PURE__*/jsx(SubNavigation, {
                  pageDescriptor: subNavigationRoot,
                  currentPageDescriptor: currentPage
                }), /*#__PURE__*/jsx(Pill, {
                  onClick: () => {
                    handleClose();
                    buttonRef.current?.focus();
                  },
                  className: srCloseButton,
                  children: "Luk navigation"
                })]
              })
            })
          })
        }, subNavigationRoot?.id)
      })
    })]
  });
});
function CourseRevisionButtons(props) {
  const location = useLocation();
  const user = useUserIdentityState(state => state.user);
  const isEditingRevision = useCourseRevisionState(state => state.isEditing);
  const areCourseRevisionsEnabled = pm2ConfigStore.useSelector(state => state.config.settings.showCoursePlanner);
  const sitemap = pm2SitemapStore.useSelector(state => state);
  const activePage = findRenderedDescriptorByPath(sitemap, location.pathname);
  const courseDescriptor = findCourseDescriptorFromParents(sitemap, activePage);
  const saveRevision = useSaveRevision(courseDescriptor);
  const canSaveRevision = useCourseRevisionState(state => disabledIdsToKey(state.disabledIds) !== disabledIdsToKey(state.editableRevision?.disabledContentIds));
  if (!areCourseRevisionsEnabled || !courseDescriptor || user?.role !== "teacher") {
    return null;
  }
  return /*#__PURE__*/jsx(TransitionGroup, {
    component: "div",
    className: revisionsWrapper,
    children: isEditingRevision && /*#__PURE__*/jsx(CSSRefTransition, {
      classNames: pillCls,
      timeout: revisionButtonFadeDurationMs,
      children: ref => /*#__PURE__*/jsx(Tooltip, {
        body: canSaveRevision ? "Gem din version" : "Du skal tilpasse forløbet før du kan gemme",
        ariaType: "description",
        placement: "right",
        containerRef: ref,
        children: ref => /*#__PURE__*/jsx(Pill, {
          ref: ref,
          className: cx(pillCls, !props.isOpen && "visible"),
          icon: /*#__PURE__*/jsx(FloppyIcon, {}),
          onClick: () => saveRevision(),
          disabled: !canSaveRevision,
          children: "Gem"
        })
      })
    })
  });
}

export { SubNavigationButton };
//# sourceMappingURL=sub-navigation.js.map
